import React from 'react'
import { Download, FileText } from 'lucide-react'
import { Button } from 'components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import type { EquitySubmission } from './types'

type DocumentKeyEntry = string | { key?: string; name?: string }

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return 'Pending'
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    })
  } catch {
    return 'Invalid Date'
  }
}

const filenameFromKey = (key: string): string => {
  const segments = key.split('/')
  return segments[segments.length - 1] || key
}

const ensurePdfFilename = (name: string): string => {
  const normalized = name.trim() || 'document.pdf'
  return normalized.toLowerCase().endsWith('.pdf')
    ? normalized
    : `${normalized}.pdf`
}

const downloadFile = async (url: string, filename?: string) => {
  try {
    const response = await fetch(url, { credentials: 'include' })
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`)
    }
    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    if (filename) link.download = filename
    link.click()
    URL.revokeObjectURL(objectUrl)
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}

type NormalizedDoc = { name: string; key: string }

const normalizeDocuments = (
  keys: Array<DocumentKeyEntry> | undefined,
): NormalizedDoc[] => {
  if (!Array.isArray(keys)) return []
  return keys
    .map((entry) => {
      const key = typeof entry === 'string' ? entry : entry?.key ?? ''
      if (!key) return null
      const filename = filenameFromKey(key)
      const displayName =
        typeof entry === 'string'
          ? filename || entry
          : entry?.name || filename || 'Document'
      return { name: displayName, key }
    })
    .filter((d): d is NormalizedDoc => d !== null)
}

type Props = {
  open: boolean
  dealId: string
  submission: EquitySubmission | null
  onClose: () => void
}

const DocumentsModal: React.FC<Props> = ({
  open,
  dealId,
  submission,
  onClose,
}) => {
  const backendBase = process.env.REACT_APP_BACKEND_URL
  const docs = normalizeDocuments(submission?.data?.documentKeys)
  const legalName = submission?.data?.name ?? 'Participant'
  const privyId = submission?.privy_id ?? ''
  const signedAt = submission?.data?.signedAt ?? null

  const buildSingleDocumentUrl = (key: string): string | null => {
    if (!backendBase || !dealId || !privyId) return null
    return `${backendBase}/api/v2/admin/deals/equity/${encodeURIComponent(
      dealId,
    )}/submissions/${privyId}/documents/download?key=${encodeURIComponent(key)}`
  }

  const buildParticipantZipUrl = (): string | null => {
    if (!backendBase || !dealId || !privyId) return null
    return `${backendBase}/api/v2/admin/deals/equity/${encodeURIComponent(
      dealId,
    )}/submissions/${privyId}/documents/download`
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-neutral-950">
            {`Documents for ${legalName}`}
          </DialogTitle>
          <div className="flex flex-col gap-1 text-xs text-neutral-500">
            {privyId && <span className="font-mono">{privyId}</span>}
            <span>Signed on: {formatDateTime(signedAt)}</span>
          </div>
        </DialogHeader>

        {docs.length === 0 ? (
          <div className="rounded-md border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-600">
            No documents are available yet for this participant.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              {docs.map((doc, idx) => (
                <div
                  key={`${doc.key}-${idx}`}
                  className="flex items-center justify-between gap-3 rounded-md border border-neutral-200 bg-white px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-neutral-600" />
                    <span className="text-sm text-neutral-900">{doc.name}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs text-neutral-900"
                    onClick={() => {
                      const url = buildSingleDocumentUrl(doc.key)
                      if (!url) return
                      downloadFile(url, ensurePdfFilename(doc.name))
                    }}
                  >
                    Download
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end">
              <Button
                variant="outline"
                className="gap-2 text-neutral-900"
                title="Download ZIP"
                onClick={() => {
                  const url = buildParticipantZipUrl()
                  if (!url) return
                  downloadFile(url, `equity-docs-${privyId}.zip`)
                }}
              >
                <Download className="w-4 h-4" />
                Download ZIP
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default DocumentsModal
